import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, BarChart3, Bot, CalendarDays, ChefHat, MessageSquare, Phone, Shield, Users } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { fetchChatStats } from "../chat/chatApi";

type Tile = { slug: string; name: string; blurb: string; Icon: typeof MessageSquare };

const ALL: Tile[] = [
  { slug: "sms",     name: "SMS",         blurb: "Twilio conversations, compose, blasts, contacts.",        Icon: MessageSquare },
  { slug: "voice",   name: "Voice",       blurb: "AI voice agent call log + voicemail.",                    Icon: Phone },
  { slug: "chat",    name: "Chatbot",     blurb: "Widget conversations + workflow health.",                 Icon: Bot },
  { slug: "food",    name: "Food Cost",   blurb: "Vendors, invoices, menu costing, analysis.",              Icon: ChefHat },
  { slug: "members", name: "Membership",  blurb: "Square subscriptions, notes, renewal emails.",            Icon: Users },
  { slug: "tips",    name: "Tips",         blurb: "Tip calculator for bartenders + axe coaches, pay periods, reports.", Icon: Banknote },
  { slug: "events",  name: "Events",      blurb: "Group event inquiries, quotes, calendar, reminders.",     Icon: CalendarDays },
  { slug: "reports", name: "Reports",     blurb: "Cross-module analytics — call dispositions + SMS volume.", Icon: BarChart3 },
  { slug: "admin",   name: "Admin",       blurb: "Users, module access, audit log.",                        Icon: Shield },
];

export function Launcher() {
  const { me, hasModule } = useAuth();
  const [unansweredCount, setUnansweredCount] = useState(0);

  useEffect(() => {
    if (!me || !hasModule("chat")) return;
    fetchChatStats()
      .then((s) => setUnansweredCount(s.unansweredMessages))
      .catch(() => {});
  }, [me, hasModule]);

  if (!me) return null;

  const tiles = ALL.filter((t) => hasModule(t.slug));

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome, {me.display_name?.split(" ")[0] ?? me.email.split("@")[0]}
        </h1>
        <p className="mt-1 text-sm text-tribal-muted">
          {me.is_admin ? "You're an admin — all modules are available." : `You have access to ${tiles.length} module${tiles.length === 1 ? "" : "s"}.`}
        </p>
      </div>

      {tiles.length === 0 ? (
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-6 text-sm text-tribal-muted">
          No modules assigned yet. Ask an admin to grant access.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map(({ slug, name, blurb, Icon }) => {
            const badge = slug === "chat" && unansweredCount > 0 ? unansweredCount : 0;
            const displayBlurb =
              badge > 0
                ? `${badge} unanswered question${badge !== 1 ? "s" : ""} need attention.`
                : blurb;
            return (
              <Link
                key={slug}
                to={`/${slug}`}
                className={`group rounded-xl border bg-tribal-surface p-5 transition-colors hover:border-tribal-red/60 hover:bg-white/[0.03] ${
                  badge > 0
                    ? "border-amber-500/40"
                    : "border-tribal-border"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-tribal-red/10 text-tribal-red group-hover:bg-tribal-red group-hover:text-white">
                    <Icon className="h-5 w-5" aria-hidden />
                    {badge > 0 && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {badge > 9 ? "9+" : badge}
                      </span>
                    )}
                  </div>
                  <div className="font-medium">{name}</div>
                </div>
                <p className={`mt-3 text-sm ${badge > 0 ? "text-amber-400" : "text-tribal-muted"}`}>
                  {displayBlurb}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
